import {
  type GetExamLocationRequestInfoQuery,
  useGetExamLocationRequestInfoQuery,
} from '@exam-center/graphql';
import { Loading } from '@shared/components';
import dayjs from 'dayjs';
import { type PropsWithChildren, createContext, useContext, useMemo } from 'react';

export type ExamLocationRequestInfo =
  GetExamLocationRequestInfoQuery['ecGetExamLocationRequestInfoById']['examLocationRequestInfo'];

interface ExamLocationRequestDetailsContextValue {
  examLocationRequestInfo: ExamLocationRequestInfo;
  /**
   * Exam rooms sorted by createdAt (newest first) for consistent display order
   */
  sortedExamRooms: ExamLocationRequestInfo['examRoomRequestInfos'];
  /**
   * Maps room request ID to its display index (1-based in UI via index + 1)
   */
  roomIndexMap: Record<string, number>;
}

interface ExamLocationRequestDetailsProviderProps extends PropsWithChildren {
  examLocationRequestInfoId: string;
}

const ExamLocationRequestDetailsContext = createContext<
  ExamLocationRequestDetailsContextValue | undefined
>(undefined);

export function useExamLocationRequestDetails() {
  const ctx = useContext(ExamLocationRequestDetailsContext);
  if (!ctx) {
    throw new Error(
      'useExamLocationRequestDetails must be used within <ExamLocationRequestDetailsProvider />',
    );
  }
  return ctx;
}

export default function ExamLocationRequestDetailsProvider(
  props: Readonly<ExamLocationRequestDetailsProviderProps>,
) {
  const { examLocationRequestInfoId, children } = props;

  const { data, loading } = useGetExamLocationRequestInfoQuery({
    variables: {
      getExamLocationRequestInfoByIdInput: {
        id: examLocationRequestInfoId,
      },
    },
    skip: !examLocationRequestInfoId,
  });

  const examLocationRequestInfo = data?.ecGetExamLocationRequestInfoById?.examLocationRequestInfo;

  const contextValue = useMemo<ExamLocationRequestDetailsContextValue | undefined>(() => {
    if (!examLocationRequestInfo) return undefined;

    // Sort rooms by createdAt (newest first) for consistent display order across all components
    const sortedExamRooms = [...examLocationRequestInfo.examRoomRequestInfos].sort((a, b) =>
      dayjs(b.createdAt).isAfter(dayjs(a.createdAt)) ? -1 : 1,
    );

    // Create a map of room ID to its display index for error messages and other components
    const roomIndexMap = sortedExamRooms.reduce(
      (acc, room, index) => {
        acc[room.id] = index;
        return acc;
      },
      {} as Record<string, number>,
    );

    return { examLocationRequestInfo, sortedExamRooms, roomIndexMap };
  }, [examLocationRequestInfo]);

  if (loading) {
    return <Loading sx={{ height: '100dvh' }} />;
  }

  if (!contextValue) {
    return null;
  }

  return (
    <ExamLocationRequestDetailsContext.Provider value={contextValue}>
      {children}
    </ExamLocationRequestDetailsContext.Provider>
  );
}

export { ExamLocationRequestDetailsContext };
